import React, { useState } from 'react';

import LoadingImage from '../../LoadingImage';
import { icons } from '../../../../helpers/icons';
import { TCoursePreview } from '../../../../types';
import { useAppSelector } from '../../../../hooks/hooks';
import {
  formatToSimpleDateTime,
  getSubscriptionIcon,
  isClassSubscriptionIncluded,
} from '../../../../helpers';
import Tag from '../../Tag';
import WaitlistConfirmationPopup from '../../WaitlistConfirmationPopup';
import StackTagsSelect from '../StackTagsSelect/StackTagsSelect';

interface ICourseCard {
  coursePreview: TCoursePreview;
  sfLearnitCourseId?: string;
  isBooked?: boolean;
  isFull?: boolean;
  isWaitlisted?: boolean;
  bookLabel?: string;
  subtitle?: string;
  onClick?: () => void;
  onWaitlistJoined?: () => void;
}

const CourseCard: React.FC<ICourseCard> = ({
  coursePreview,
  sfLearnitCourseId,
  isBooked,
  isFull,
  isWaitlisted: initialIsWaitlisted,
  bookLabel,
  subtitle,
  onClick,
  onWaitlistJoined,
}) => {
  const [waitlistPopupOpen, setWaitlistPopupOpen] = useState(false);
  const [isWaitlisted, setIsWaitlisted] = useState(initialIsWaitlisted);

  const { user } = useAppSelector((state) => state.userReducer);

  const included = isClassSubscriptionIncluded(
    coursePreview.nearestStartTime,
    user?.program,
  );

  const renderPrice = () => {
    if (!coursePreview.priceRange) {
      return 'No upcoming classes';
    }

    if (included) {
      return (
        <>
          <div className="text-[11px] leading-none">
            <span>From </span>
            <span className="line-through">{coursePreview.priceRange}</span>
          </div>
          <div className="font-bold">$0.00</div>
        </>
      );
    }
    return `From ${coursePreview.priceRange}`;
  };

  const showSubscription = coursePreview.priceRange && included;
  const subscriptionIcon = user?.program
    ? getSubscriptionIcon(user.program)
    : null;

  const renderCourseStatus = () => {
    if (onClick && isFull && !isWaitlisted) {
      return <div className="w-full">{tagComponents.waitlistOnly}</div>;
    }

    return (
      <>
        <div className="flex items-center gap-1 text-core-dark whitespace-nowrap">
          <img src={icons.online} alt="Live Online" />
          <span>Live Online</span>
        </div>
        {onClick ? (
          <>
            {!isBooked && !isFull
              ? tagComponents.notBooked
              : isBooked
              ? tagComponents.alreadyBooked
              : isWaitlisted
              ? tagComponents.waitlisted
              : null}
          </>
        ) : isBooked ? (
          tagComponents.alreadyBooked
        ) : isFull ? (
          tagComponents.full
        ) : (
          // Visible on Public Courses
          <div className="pl-2 text-right text-blue-primary">
            {renderPrice()}
          </div>
        )}
      </>
    );
  };

  // Tag components for different states
  const tagComponents = {
    notBooked: (
      <div
        className="cursor-pointer text-blue-primary hover:underline"
        onClick={onClick}
      >
        {bookLabel || 'Book this course'}
      </div>
    ),
    alreadyBooked: (
      <Tag text="Already booked" color="yellow" className="self-start" />
    ),
    waitlisted: (
      <Tag text="Waitlisted" color="neutral" className="self-start" />
    ),
    full: <Tag text="Full" color="yellow" className="self-start" />,
    waitlistOnly: (
      <div className="flex flex-col w-full gap-1">
        <Tag text="Waitlist only" color="yellow" className="self-start" />
        <div
          className="text-sm text-right cursor-pointer text-blue-primary hover:underline"
          onClick={() => setWaitlistPopupOpen(true)}
        >
          Join waitlist
        </div>
      </div>
    ),
  };

  return (
    <>
      {showSubscription && subscriptionIcon && (
        <div className="w-6 h-6 absolute end-0 mr-2 mt-2 rounded bg-white p-[1px]">
          <img src={subscriptionIcon} alt="" />
        </div>
      )}
      <LoadingImage
        className="object-cover w-full h-40 rounded-t-2xl"
        src={coursePreview.imageUrl ?? icons.coursePlaceholder}
        alt={coursePreview.name}
      />
      <div className="relative flex flex-col flex-grow gap-2 p-4 font-medium">
        <div className="text-lg font-bold text-core-dark">
          {coursePreview.name}
        </div>
        {(subtitle || coursePreview.nearestStartTime) && (
          <div className="font-bold uppercase text-regular text-core-dark">
            {subtitle || formatToSimpleDateTime(coursePreview.nearestStartTime)}
          </div>
        )}
        <div
          className="text-regular text-grey-500 line-clamp-3"
          dangerouslySetInnerHTML={{ __html: coursePreview.whoShouldAttend }}
        ></div>
        <div className="flex justify-between text-regular">
          {renderCourseStatus()}
        </div>

        {coursePreview.skillStacks && coursePreview.skillStacks.length > 0 && (
          <div
            className="mt-auto -mx-4 -mb-4 border-t border-grey-100"
            onClick={(e) => e.preventDefault()}
          >
            <div className="px-4 py-1">
              <div className="mb-1 text-grey-500 text-regular">
                Skill stacks
              </div>
              <StackTagsSelect
                stacks={coursePreview.skillStacks.map((stack) => {
                  return {
                    id: stack,
                    name: stack,
                  };
                })}
              />
            </div>
          </div>
        )}
      </div>

      {waitlistPopupOpen && (
        <WaitlistConfirmationPopup
          open={waitlistPopupOpen}
          setOpen={setWaitlistPopupOpen}
          coursePreview={coursePreview}
          sfLearnitCourseId={sfLearnitCourseId}
          onWaitlistJoined={() => {
            setIsWaitlisted(true);
            if (onWaitlistJoined) {
              onWaitlistJoined();
            }
          }}
        />
      )}
    </>
  );
};

export default CourseCard;
